import { ComponentProps } from "react";
import { BlockTitle } from "@blocks/Blocks";
import { useTrimmedBreakingNews } from "@hooks/useTrimmedBreakingNews";
import Card from "@particles/card/Card";
import { sectionNavigationItems, shortTimestampFormat } from "@util/constant";
import { cn, getFormattedTime } from "@util/helpers";

export type BreakingNewsProps = {
  rootClassName?: string;
  showHeading?: boolean;
  showBorder?: boolean;
  blockTitle?: string;
  blockLink?: string;
};

const section = sectionNavigationItems["breaking-news"];

export default function BreakingNews({
  rootClassName,
  showHeading = true,
  showBorder = true,
  blockTitle,
  blockLink,
}: BreakingNewsProps): React.ReactElement {
  const data = useTrimmedBreakingNews() || [];

  return (
    <>
      {data.length > 0 ? (
        <section className={rootClassName} data-testid="breakingnews-component">
          {showHeading ? (
            <BlockTitle
              link={blockLink ?? section?.link}
              text={blockTitle ?? section?.label?.toUpperCase()}
              rootLinkClassName="!text-black"
            />
          ) : null}

          <div
            className={cn("stories", {
              "divide divide-y": !!showBorder,
            })}
          >
            {data?.map((article: ComponentProps<typeof Card>) => {
              return (
                <div
                  className={cn("story flex flex-row items-start", {
                    "py-4": !!showBorder,
                    "py-2": !showBorder,
                  })}
                  key={article.id}
                >
                  <article className={cn("flex h-full flex-row")}>
                    <div className="mr-4 mt-1.5 w-[60px] flex-shrink-0 whitespace-nowrap font-poppins text-3xs font-medium text-gray-550">
                      {getFormattedTime(
                        article.editedTime,
                        shortTimestampFormat
                      )}
                    </div>

                    <Card
                      {...article}
                      className="mt-[calc(0.375rem-1px)]"
                      editedTime={undefined}
                      variations={{
                        title: {
                          size: "text-base",
                          color: "my-1 font-lct font-bold text-base",
                        },
                        subscriberLabel: {
                          extraClass: "mb-1",
                        },
                      }}
                    />
                  </article>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
    </>
  );
}
